import { cartSnapshots, getItemCountFromSections, beginCartMutation, isCartBusy, waitForCartReady, blockCartCheckout } from './cart-state.js';
import { prepareKitPurchase } from './purchase-discount.js';
/// <reference path="../@types/global.d.ts" />
import { buildKitCartItems } from './kit-cart-items.js';
import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { getFreeProcessingDiscountCodeForVariant, getPrimaryDiscountCodeFromUrl } from '@theme/free-processing-discount';
import { validateStorageSelection } from './storage-selection-validation.js';

/**
 * Clears the cart without triggering section updates.
 * @returns {Promise<any>}
 */
async function clearCart() {
  const clearMarker = cartPerformance.createStartingMarker('clear-cart-before-add:user-action');

  try {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) throw new Error(`Cart clear failed (${response.status})`);
    const cart = await response.json();
    if (cart.item_count !== 0) throw new Error('Cart was not cleared');
    Theme.legacyBox.cart = cart;

    return cart;
  } catch (error) {
    console.error('Error clearing cart before add-to-cart:', error);
    throw error;
  } finally {
    cartPerformance.measureFromMarker(clearMarker);
  }
}

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const CLOUD_CONFIG_SELECTOR = 'script[data-legacybox-cloud-product-config]';

/**
 * Photobox parent variant id → standard processing variant id
 * @type {Record<string, string>}
 */
const PHOTOBOX_PARENT_VARIANT_TO_STANDARD_PROCESSING = {
  39775547719754: '32546993897546', // 200 photos
  39775547752522: '32546995732554', // 1000 photos
  39775547785290: '32546996420682', // 2000 photos
  39775547818058: '32546996781130', // 5000 photos
};

const DEFAULT_STANDARD_PROCESSING_VARIANT_ID = '32546995732554';

/**
 * Resolves the configured cloud add-on selected for the current PDP visit.
 *
 * @returns {{
 *   enabled?: boolean,
 *   productHandle?: string,
 *   productId?: string | number,
 *   variantId?: string | number,
 *   sellingPlanId?: string | number
 * } | null}
 */
function getCloudProductConfig(root = document) {
  const normalizeConfig = (config) => {
    const productHandle = typeof config?.productHandle === 'string' ? config.productHandle : '';
    const variantId = Number(config?.variantId);
    const sellingPlanId = Number(config?.sellingPlanId);

    if (!config?.enabled || !productHandle || !Number.isFinite(variantId) || variantId <= 0)
      return null;
    if (!Number.isFinite(sellingPlanId) || sellingPlanId <= 0) return null;

    return {
      enabled: true,
      productHandle,
      productId: config.productId,
      variantId,
      sellingPlanId,
    };
  };

  const explicitConfig = root === document ? normalizeConfig(window.legacybox?.cloudProduct) : null;
  if (explicitConfig) return explicitConfig;

  const configElements = root.querySelectorAll(CLOUD_CONFIG_SELECTOR);

  for (const configElement of configElements) {
    try {
      const parsedConfig = JSON.parse(configElement.textContent || '{}');
      const normalizedConfig = normalizeConfig(parsedConfig);

      if (normalizedConfig) return normalizedConfig;
    } catch {
      // Continue to any later config node. Theme editor re-rendering can leave stale HTML.
    }
  }

  return null;
}

/**
 * Builds the cloud add-on cart line from the configured cloud product.
 *
 * @param {number} localId
 * @param {string | number | null | undefined} parentId
 * @param {ReturnType<typeof getCloudProductConfig>} cloudConfig
 * @returns {any | null}
 */
function buildCloudCartItem(localId, parentId, cloudConfig) {
  if (!cloudConfig?.enabled) return null;

  const properties = {
    _localId: localId,
  };

  properties._cloud_product_handle = cloudConfig.productHandle || '';
  properties._cloud_variant_id = String(cloudConfig.variantId);
  properties._cloud_selling_plan_id = String(cloudConfig.sellingPlanId);

  return {
    id: Number(cloudConfig.variantId),
    properties,
    quantity: 1,
    ...(parentId ? { parent_id: Number(parentId) } : {}),
    selling_plan: Number(cloudConfig.sellingPlanId),
  };
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
      );
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!this.#validateStorageSelection(form, event)) return;
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest('product-form-component')
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  /**
   * Validates storage option selection and shows an inline error if needed.
   * @param {HTMLFormElement | null} form
   * @param {MouseEvent & {target: HTMLElement}} event
   * @returns {boolean} Whether storage selection is valid and processing can continue.
   */
  #validateStorageSelection(form, event) {
    return validateStorageSelection({
      storageOptionsScope: this.closest('product-details') ?? undefined,
      button: form?.querySelector('[ref="addToCartButton"]') ?? undefined,
      errorScope: form ?? undefined,
      preventDefaultEvent: event,
      scrollTarget: undefined,
    });
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** Prevents concurrent add-to-cart while an in-flight request is pending. */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString()
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector('quantity-selector-component')
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === 'product-form-component'
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Finds the active free-processing discount code for a selected parent variant.
   *
   * @param {string | null | undefined} variantId
   * @returns {string | null}
   */
  #getFreeProcessingDiscountCode(variantId) {
    return variantId ? getFreeProcessingDiscountCodeForVariant(variantId) : null;
  }

  /**
   * Builds the cart payload for adding items based on active kit selection and processing time.
   *
   * @param {string[]} cartItemComponentsSectionIds
   * @param {ReturnType<typeof getCloudProductConfig>} cloudConfig
   * @returns {{ items: Array<any>, sections: string }}
   */
  #buildCartPayload(cartItemComponentsSectionIds, cloudConfig) {
    const parentId = document.querySelector('[ref="cards[]"][data-active-kit="true"]')?.getAttribute('data-var-id');
    const variantIds = Array.from(document.querySelectorAll('[data-active-kit="true"]')).map(div => div.getAttribute('data-var-id'));
    const processingId = window.legacybox?.selectedProcessingTime?.id ||
      document.querySelector('[data-digitizing-time] [data-time="standard"]')?.getAttribute('data-time-id');
    const localId = Math.floor(Math.random() * 1000000);
    const itemsToAdd = buildKitCartItems(parentId, variantIds, null, localId, this.#getFreeProcessingDiscountCode(parentId));
    const cloudItem = buildCloudCartItem(localId, parentId, cloudConfig);
    if (cloudItem) itemsToAdd.push(cloudItem);
    if (processingId) itemsToAdd.push(buildKitCartItems(parentId, [], processingId, localId, null)[1]);

    return {
      items: itemsToAdd,
      sections: cartItemComponentsSectionIds.join(','),
    };
  }

  /**
   * Builds the cart payload for adding items based on selected variant.
   *
   * @param {string[]} cartItemComponentsSectionIds
   * @param {ReturnType<typeof getCloudProductConfig>} cloudConfig
   * @returns {{ items: Array<any>, sections: string }}
   */
  #buildVariantPayload(cartItemComponentsSectionIds, cloudConfig) {
    const itemsToAdd = [];
    const localId = Math.floor(Math.random() * 1000000);
    const parentId = this.refs.variantId?.value;
    const freeProcessingDiscountCode = this.#getFreeProcessingDiscountCode(parentId);

    const selectedVariant = {
      id: Number(parentId),
      properties: {
        _localId: localId,
        ...(freeProcessingDiscountCode
          ? { _freeProcessingDiscountCode: freeProcessingDiscountCode }
          : {}),
      },
      quantity: 1,
    };
    itemsToAdd.push(selectedVariant);

    const cloudItem = buildCloudCartItem(localId, parentId, cloudConfig);
    if (cloudItem) {
      itemsToAdd.push(cloudItem);
    }

    const processingVariantId =
      (parentId && PHOTOBOX_PARENT_VARIANT_TO_STANDARD_PROCESSING[parentId]) ||
      DEFAULT_STANDARD_PROCESSING_VARIANT_ID;

    // add selected processing time
    const processingItem = {
      id: processingVariantId ? Number(processingVariantId) : undefined,
      properties: {
        _localId: localId,
      },
      quantity: 1,
      ...(parentId && parentId !== processingVariantId ? { parent_id: Number(parentId) } : {}),
    };

    itemsToAdd.push(processingItem);

    return {
      items: itemsToAdd,
      sections: cartItemComponentsSectionIds.join(','),
    };
  }

  /**
   * Clears cart if needed, builds payload, POSTs to cart add, handles response.
   * Stop before adding if the existing cart cannot be cleared or its purchase offer cannot be selected.
   *
   * @param {Event} _event - Reserved for parity with caller (performance measured in handleSubmit).
   * @param {HTMLFormElement} form
   * @param {NodeListOf<AddToCartComponent>} allAddToCartContainers
   */
  async #performCartAdd(_event, form, allAddToCartContainers) {
    const { addToCartTextError } = this.refs;

    const standaloneKit = form.hasAttribute('data-kit-purchase');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const hasKitItems =
      document.querySelector('product-kit-selection [data-active-kit="true"]') !== null;
    const hasVariantItems = document.querySelector('.product-details variant-picker') !== null;
    const cloudConfig = hasKitItems || hasVariantItems ? getCloudProductConfig() : null;

    let fetchCfg;
    if (standaloneKit) {
      const parent = formData.get('id');
      const outputs = formData.getAll('kit_output');
      const processing = formData.get('kit_processing');
      if (!outputs.length || !processing) throw new Error('Choose an available kit and at least one storage option.');
      const payload = {
        items: [],
        sections: cartItemComponentsSectionIds.join(','),
      };
      const localId = Math.floor(Math.random() * 1000000);
      const kitCloud = getCloudProductConfig(form);
      if (!kitCloud) throw new Error('Membership is unavailable. Please try again shortly.');
      payload.items = buildKitCartItems(parent, outputs, null, localId, this.#getFreeProcessingDiscountCode(String(parent)));
      payload.items.push(buildCloudCartItem(localId, parent, kitCloud));
      payload.items.push(buildKitCartItems(parent, [], processing, localId, null)[1]);
      fetchCfg = fetchConfig('json', { body: JSON.stringify(payload) });
    } else if (hasKitItems) {
      // Use kit-specific payload for products with kit items
      const payload = this.#buildCartPayload(cartItemComponentsSectionIds, cloudConfig);
      fetchCfg = fetchConfig('json', { body: JSON.stringify(payload) });
    } else if (hasVariantItems) {
      const payload = this.#buildVariantPayload(cartItemComponentsSectionIds, cloudConfig);
      fetchCfg = fetchConfig('json', { body: JSON.stringify(payload) });
    } else {
      // Use standard FormData for non-kit products
      formData.append('sections', cartItemComponentsSectionIds.join(','));
      fetchCfg = fetchConfig('javascript', { body: formData });
    }

    // Validate and build the full selection before changing the existing cart.
    // Both the PDP and standalone bar use one confirmed replacement and the
    // same purchase discount resolver (including a stale page-load cart).
    let preparationMutated = false;
    try {
      Theme.legacyBox.cart = await prepareKitPurchase({ config: Theme.legacyBox.discounts,
        template: this.dataset.purchaseTemplate || Theme.template?.name, storage: sessionStorage,
        getRequestedCode: getPrimaryDiscountCodeFromUrl, cart: Theme.legacyBox.cart,
        clearCart: async () => { preparationMutated = true; return clearCart(); }, cartUpdateUrl: Theme.routes.cart_update_url });
    } catch (error) {
      console.error(error);
      if (preparationMutated) blockCartCheckout();
      const message = 'We could not update your cart. Please try again.';
      this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message, message));
      // Preparation may have cleared the cart before a discount-update failure.
      this.dispatchEvent(new CartAddEvent({}, this.id, {
        didError: true, source: 'product-form-component', productId: this.dataset.productId,
      }));
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        addToCartTextError.textContent = message;
      }
      this.#setLiveRegionText(message);
      if (standaloneKit) throw error;
      return;
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchCfg);
      const textBody = await response.text();
      const json = textBody ? JSON.parse(textBody) : {};

      if (!response.ok || json.status) {
        if (standaloneKit) {
          throw new Error(json.description || json.message || 'Unable to add this kit. Please try again.');
        }
        this.dispatchEvent(
          new CartErrorEvent(
            form.getAttribute('id') || '',
            json.message,
            json.description,
            json.errors
          )
        );

        if (!addToCartTextError) return;
        addToCartTextError.classList.remove('hidden');

        // Reuse the text node if the user is spam-clicking
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = json.message;
        } else {
          const newTextNode = document.createTextNode(json.message);
          addToCartTextError.appendChild(newTextNode);
        }

        // Create or get existing error live region for screen readers
        this.#setLiveRegionText(json.message);

        this.#timeout = setTimeout(() => {
          if (!addToCartTextError) return;
          addToCartTextError.classList.add('hidden');

          // Clear the announcement
          this.#clearLiveRegionText();
        }, ERROR_MESSAGE_DISPLAY_DURATION);

        // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
        // because our back-end still adds the max allowed amount to the cart.
        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
          })
        );

        return;
      }

      const id = formData.get('id');

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      if (!id) throw new Error('Form ID is required');

      // Add aria-live region to inform screen readers that the item was added
      // Get the added text from any add-to-cart button
      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      // The add response already contains authoritative drawer HTML. Open it immediately.
      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
          absoluteItemCount: getItemCountFromSections(json.sections),
          productId: this.dataset.productId,
          sections: json.sections,
        })
      );
      // Cart-discount initialization shares this bounded read. Failure cannot undo a successful add.
      void cartSnapshots.read().then(cart => {
        Theme.legacyBox.cart = cart;
        this.#updateCartQuantityFromData(cart);
      }).catch(error => console.error('Unable to refresh added cart:', error));
      return true;
    } catch (error) {
      blockCartCheckout();
      if (standaloneKit) {
        // Clearing and adding are separate requests. Reconcile the drawer even
        // when the add fails after clearing or Shopify only adds part of a kit.
        this.dispatchEvent(new CartErrorEvent(form.id || '', error.message, error.message, {}));
        this.dispatchEvent(new CartAddEvent({}, this.id, {
          didError: true, source: 'product-form-component', productId: this.dataset.productId,
        }));
        throw error;
      }
      console.error(error);
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (this.#isSubmitting) return;

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace(
          '{{ maximum }}',
          validation.maxQuantity?.toString() || ''
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const stickyBtn = /** @type {HTMLButtonElement | null} */ (
      document.querySelector('sticky-add-to-cart [ref="addToCartButton"]')
    );
    this.#isSubmitting = true;
    for (const container of allAddToCartContainers) {
      container.disable();
    }
    if (stickyBtn) stickyBtn.disabled = true;

    let endMutation = () => {};
    try {
      while (isCartBusy()) {
        this.#setLiveRegionText(Theme.translations?.checking_offer || 'Updating your offer…');
        await waitForCartReady();
      }
      endMutation = beginCartMutation();
      return await this.#performCartAdd(event, form, allAddToCartContainers);
    } catch (error) {
      this.#setLiveRegionText(error.message);
      throw error;
    } finally {
      endMutation();
      this.#isSubmitting = false;
      for (const container of allAddToCartContainers) {
        container.enable();
      }
      if (stickyBtn) stickyBtn.disabled = false;
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        /** @type {Element} */ (newElement.cloneNode(true))
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]'
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(selector),
          fallback
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
